import { For, Show, Switch, Match, createContext, createEffect, createMemo, createSignal, useContext, Accessor, Setter, Signal } from "solid-js";
import styles from './k12-hallpass-stuhallpasssummary.module.css'
import { Button, requireViewContext, STComponentWrapperProps, Spinner as StSpinner } from "st-app";
import K12HallpassStudentrestrictions from "../k12-hallpass-studentrestrictions/k12-hallpass-studentrestrictions";
import K12HallpassStuhallpasses from "../k12-hallpass-stuhallpasses/k12-hallpass-stuhallpasses";
import { Card as StCard } from "st-app";
import { createProxySignal, debounce } from "common";
import { k12HallpassStuhallpasssummary as api, StudentHallPassSummaryData, StudentHallPassStatsData } from 'sis-app/api'

type FilterTypes = "Today" | "ThisWeek" | "ThisMonth" | "ThisSchoolYear"

class K12HallpassStuhallpasssummaryContext {
    filterType: Accessor<FilterTypes>; setFilterType: Setter<FilterTypes>
    ssyGU: Accessor<string>;
    overLimitFilter: Accessor<boolean>; setOverLimitFilter: Setter<boolean>
    showMorePasses: Accessor<boolean>; setShowMorePasses: Setter<boolean>
    passCount: Accessor<number>; setPassCount: Setter<number>
    svue: Accessor<boolean>;

    constructor(ssyGU: Accessor<string>, svue: Accessor<boolean>) {
        this.ssyGU = ssyGU;
        this.svue = svue;
        [this.filterType, this.setFilterType] = createSignal<FilterTypes>("ThisSchoolYear");
        [this.overLimitFilter, this.setOverLimitFilter] = createSignal<boolean>(false);
        [this.showMorePasses, this.setShowMorePasses] = createSignal<boolean>(false);
        [this.passCount, this.setPassCount] = createSignal<number>(0);
    }
}

class K12HallpassStuhallpasssummaryProps extends STComponentWrapperProps {
    context?: K12HallpassStuhallpasssummaryContext
    svue?: boolean = false
}

const k12HallpassStuhallpasssummaryContext = createContext<K12HallpassStuhallpasssummaryContext>();
export const useK12HallpassStuhallpasssummaryContext = () => useContext(k12HallpassStuhallpasssummaryContext)!;

export default function K12HallpassStuhallpasssummary(props: K12HallpassStuhallpasssummaryProps) {
    const ssyGU = createMemo(() => props.value ?? '');
    const svue = createMemo(() => props.svue ?? false);
    const context = createMemo(() => props.context || new K12HallpassStuhallpasssummaryContext(ssyGU, svue));

    return <k12HallpassStuhallpasssummaryContext.Provider value={context()}>
        <K12HallpassStuhallpasssummaryInner {...props} />
    </k12HallpassStuhallpasssummaryContext.Provider>
}
K12HallpassStuhallpasssummary.defaultProps = new K12HallpassStuhallpasssummaryProps() as K12HallpassStuhallpasssummaryProps;



function HallPassTimeFrameFilterCard(props: { filterType: FilterTypes, label: string, value: string, dataLoaded: boolean }) {
    const context = useK12HallpassStuhallpasssummaryContext();
    const filterActive = createProxySignal<boolean>(
        () => context.filterType() === props.filterType,
        (active) => {
            if (active) {
                context.setFilterType(props.filterType);
            }
        });

    const dataLoaded = !context.ssyGU() || props.dataLoaded;

    return <StCard label={props.label} value={props.value} filterActive={filterActive} size="small" showLoadingSpinner={true} dataLoaded={dataLoaded} />
}

function OverLimitFilterCard(props: { value: string, dataLoaded: boolean }) {
    const context = useK12HallpassStuhallpasssummaryContext();
    const filterActive = createProxySignal<boolean>(
        () => context.overLimitFilter(),
        (active) => {
            context.setOverLimitFilter(active);
        });

    return <StCard label="Over Time Limit" value={props.value} filterActive={filterActive} size="medium" showLoadingSpinner={true} dataLoaded={props.dataLoaded} />
}

function RecentHallPasses(props: { passCount: number }) {
    const context = useK12HallpassStuhallpasssummaryContext();
    const overLimitFilterActive = createProxySignal<boolean>(
        () => context.overLimitFilter(),
        (active) => {
            context.setOverLimitFilter(active);
        });

    const showMorePasses = createProxySignal<boolean>(
        () => context.showMorePasses(),
        (showMore) => {
            context.setShowMorePasses(showMore);
        });

    const timeFrame = createProxySignal<string>(
        () => context.filterType().toString(),
        (tf) => { });

    return <div>
        <K12HallpassStuhallpasses ssyGU={context.ssyGU()} overLimitFilterActive={overLimitFilterActive} showMorePasses={showMorePasses} timeFrame={timeFrame} svue={context.svue()} />
        <br />
        <Show when={!context.svue()}>
            <Show when={props.passCount > 8}>
                <ShowMoreHallPassesButton />
            </Show>
        </Show>
    </div>
}

    function ShowMoreHallPassesButton() {
        const context = useK12HallpassStuhallpasssummaryContext();

        return <Button type="primary" style="margin-left:5px;" onClick={() => context.setShowMorePasses(!context.showMorePasses())}>
            <Switch fallback="Show More">
                <Match when={context.showMorePasses()}>
                    Show Less
                </Match>
            </Switch>
        </Button>
    }

    function K12HallpassStuhallpasssummaryInner(props: K12HallpassStuhallpasssummaryProps & STComponentWrapperProps) {
        const [hallpassSummaryData, setHallpassSummaryData] = createSignal<StudentHallPassSummaryData | undefined>();

        const context = useK12HallpassStuhallpasssummaryContext();

        const [hallpassStatsData, setHallpassStatsData] = createSignal<StudentHallPassStatsData | undefined>();

        createEffect(() => {

            setHallpassSummaryData(undefined);
            setHallpassStatsData(undefined);

            if (context.ssyGU()) {
                api.LoadHallPassSummaryData(context.ssyGU())
                    .then(setHallpassSummaryData);

                api.LoadHallPassStatsData(context.ssyGU(), context.filterType().toString())
                    .then(setHallpassStatsData);
            }
        });

        const recentHallPassCount = createMemo(() => {
            if (context.overLimitFilter()) {
                return parseInt(hallpassStatsData()?.passesOverTimeLimit ?? '0')
            } else {
                switch (context.filterType()) {
                    case "Today":
                        return parseInt(hallpassSummaryData()?.countToday ?? '0')
                    case "ThisWeek":
                        return parseInt(hallpassSummaryData()?.countThisWeek ?? '0')
                    case "ThisMonth":
                        return parseInt(hallpassSummaryData()?.countThisMonth ?? '0')
                    case "ThisSchoolYear":
                        return parseInt(hallpassSummaryData()?.countThisSchoolYear ?? '0')
                }
            }
        })

        return <div class={styles.HallPassSummary}>
            <div class={styles.LeftColumn}>
                <span class={styles.HeaderLabel}>Hall Passes</span>
                <div class={styles.TimeFrameCards}>
                    <HallPassTimeFrameFilterCard label="Today" value={hallpassSummaryData()?.countToday ?? ""} filterType="Today" dataLoaded={hallpassSummaryData()?.dataLoaded ?? false} />
                    <HallPassTimeFrameFilterCard label="This Week" value={hallpassSummaryData()?.countThisWeek ?? ""} filterType="ThisWeek" dataLoaded={hallpassSummaryData()?.dataLoaded ?? false} />
                    <HallPassTimeFrameFilterCard label="This Month" value={hallpassSummaryData()?.countThisMonth ?? ""} filterType="ThisMonth" dataLoaded={hallpassSummaryData()?.dataLoaded ?? false} />
                    <HallPassTimeFrameFilterCard label="This Year" value={hallpassSummaryData()?.countThisSchoolYear ?? ""} filterType="ThisSchoolYear" dataLoaded={hallpassSummaryData()?.dataLoaded ?? false} />
                </div>
                <br />
                <Show when={context.svue() === false}>
                    <span class={styles.HeaderLabel}>Restrictions</span>
                    <K12HallpassStudentrestrictions ssyGU={context.ssyGU()} />
                </Show>
                <br />
                <span class={styles.HeaderLabel}>Other Information</span>
                <br />
                <StCard label="Hall Pass Status" size="medium"
                    value={hallpassSummaryData()?.hallPassStatus ?? ""}
                    icon={hallpassSummaryData()?.hallPassStatusIcon}
                    iconColor={hallpassSummaryData()?.hallPassStatusIconColor}
                    showLoadingSpinner={true}
                    dataLoaded={!context.ssyGU() || (hallpassSummaryData()?.dataLoaded ?? false)}
                    style={hallpassSummaryData()?.hallPassStatus === "Disabled" ? "yellow" : ""}
                />

                <StCard label="Pass Limit" size="medium"
                    value={hallpassSummaryData()?.passLimit ?? ""}
                    icon={hallpassSummaryData()?.passLimitIcon}
                    iconColor={hallpassSummaryData()?.passLimitIconColor}
                    showLoadingSpinner={true}
                    dataLoaded={!context.ssyGU() || (hallpassSummaryData()?.dataLoaded ?? false)} />
            </div>
            <div class={styles.Divider}></div>
            <div class={styles.RightColumn}>
                <span class={styles.HeaderLabel}>Summary</span>
                <div class={styles.SummaryData}>
                    <StCard label="Total Pass Time" value={hallpassStatsData()?.totalPassTime ?? ""} size="medium" showLoadingSpinner={true} dataLoaded={!context.ssyGU() || (hallpassStatsData()?.dataLoaded ?? false)} />
                    <OverLimitFilterCard value={hallpassStatsData()?.passesOverTimeLimit ?? ""} dataLoaded={!context.ssyGU() || (hallpassStatsData()?.dataLoaded ?? false)} />
                </div>
                <span class={styles.HeaderLabel}>Recent Passes</span>
                <RecentHallPasses passCount={recentHallPassCount()} />
            </div>
        </div>
    }